import React from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { useRouter } from '@common-reducers/hooks/use-router';
import ChargebeeButton from '@common-components/chargebee-button/chargebee-button';
import { shouldShowFreeTileCancelCta } from '../plan-selection/cancel-cta';
import { BRAND } from '@common/styles/brand-tokens';
import { Check, CheckCheck, ChevronDown, Cloud, Crown, Info, MonitorOff as LaptopOff, Lock, Minus, Moon, Plus, RefreshCw, Sparkles, Zap } from 'lucide-react';
import { periodFromQuery } from './period-from-query';
import { FEATURE_ORDER, FeatureId, PlanView, SEAT_OPTIONS, usePlanData } from './use-plan-data';
import { FX_DURATIONS, FX_STYLES, FxCard, MarkerHighlight, MessageFountain, ProSky, useFxDirector } from './plan-card-fx';
import { useGetGatewayImpactMutation, useRecordMcpEventMutation, useResolveMcpPricingArmMutation } from '@common-reducers/backend-api/backend-api';
import { parseMcpPricingContext, toMcpPricingArm } from './mcp-pricing-context';
import { shouldWarnGatewayLoss } from './gateway-warning';
import { GatewayLossDialog } from './gateway-loss-dialog';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

/** Idle animation order: one plan at a time, cheapest first. */
const FX_ORDER = ['Scheduler', 'Manager', 'Campaigner'];

const C = {
  ...BRAND,
  brandSolid: '#1677cc',
  subtle: 'color-mix(in srgb, var(--color-text, #1a202c) 4%, var(--color-surface, #fff))',
  // Pro's own surface: a deep night-blue that reads as "premium / always on" in light AND dark mode.
  night: 'linear-gradient(160deg, #0d3a66 0%, #0a2647 55%, #071a33 100%)',
  nightText: '#f1f5fb',
  nightMuted: 'rgba(225,235,250,.84)',
  nightLine: 'rgba(255,255,255,.14)',
};

const PLAN_ICON: Record<string, React.ElementType> = { Scheduler: Zap, Manager: Sparkles, Campaigner: Crown };

const STYLES = `
.psv2-in{animation:psv2-in .15s ease-out both}
@keyframes psv2-in{from{opacity:0}to{opacity:1}}
.psv2-card{transition:transform .2s ease,box-shadow .2s ease}
.psv2-step:hover:not(:disabled){background:${C.track}!important}
.psv2-cta{text-transform:none!important;letter-spacing:0!important;width:100%;height:46px!important;border-radius:12px!important;font-weight:650!important;font-size:15px!important;transition:transform .15s ease,box-shadow .15s ease,filter .15s ease!important}
.psv2-cta-primary{background:${C.brand}!important;color:${C.brandText}!important;border:none!important;box-shadow:${C.brandShadow}}
.psv2-cta-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:${C.brandShadowHover};filter:brightness(1.06)}
.psv2-cta-secondary{background:var(--color-surface,#fff)!important;color:${C.text}!important;border:1px solid ${C.trackStrong}!important}
.psv2-cta-secondary:hover:not(:disabled){border-color:${C.text}!important}
.psv2-cta-night{background:#ffffff!important;color:#0a2647!important;border:none!important;box-shadow:0 10px 24px -12px rgba(0,0,0,.6)}
.psv2-cta-night:hover:not(:disabled){transform:translateY(-1px);filter:brightness(.96)}
.psv2-cta-navy{background:#0a2647!important;color:#fff!important;border:none!important}
.psv2-cta-navy:hover:not(:disabled){transform:translateY(-1px);filter:brightness(1.25)}
.psv2-cta-quiet{background:transparent!important;color:${C.muted}!important;border:1px solid ${C.border}!important;box-shadow:none!important}
.psv2-cta-quiet:hover:not(:disabled){color:${C.text}!important}
.psv2-cta-busy{pointer-events:none;cursor:progress}
.psv2-cta-busy::before{content:"";flex:0 0 auto;width:16px;height:16px;margin-inline-end:8px;border-radius:50%;border:2px solid currentColor;border-inline-end-color:transparent;animation:psv2-spin .7s linear infinite}
@keyframes psv2-spin{to{transform:rotate(360deg)}}
.psv2-cta-locked{opacity:.45;pointer-events:none}
.psv2-cta:disabled{opacity:1!important;background:${C.track}!important;color:${C.muted}!important;border:none!important;box-shadow:none!important;cursor:default}
.psv2-night .psv2-cta:disabled{background:rgba(255,255,255,.14)!important;color:${C.nightMuted}!important}
.psv2-cta-sm{height:38px!important;font-size:13.5px!important;border-radius:10px!important}
.psv2-link-btn{text-transform:none!important;background:none!important;border:none!important;box-shadow:none!important;padding:0!important;height:auto!important;color:${C.muted}!important;font-size:13px!important;font-weight:500!important;text-decoration:underline;text-underline-offset:3px}
.psv2-link-btn:hover{color:${C.text}!important}
.psv2-faq summary{list-style:none;cursor:pointer}
.psv2-faq summary::-webkit-details-marker{display:none}
.psv2-faq[open] .psv2-chev{transform:rotate(180deg)}
.psv2-chev{transition:transform .2s ease}
.psv2-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:20px;align-items:stretch}
.psv2-table-wrap{overflow-x:auto}
.psv2-flow{animation:psv2-flow 1.2s linear infinite}
@keyframes psv2-flow{from{background-position:0 0}to{background-position:8px 0}}
[dir=rtl] .psv2-flow{animation-direction:reverse}
@media (max-width:960px){.psv2-grid{grid-template-columns:1fr;max-width:460px;margin-inline:auto}.psv2-popular{order:-1}}
@media (max-width:600px){.psv2-sub{display:none}.psv2-controls{gap:10px!important;margin:20px 0 28px!important}.psv2-controls button{padding:0 12px!important}.psv2-seat-label{min-width:64px!important}}
@media (prefers-reduced-motion:reduce){.psv2-in,.psv2-card,.psv2-flow{animation:none;transition:none}}
`;

const money = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(1)}`);

export const PlanSelectionV2: React.FC = () => {
  const { t } = useTranslation(namespace);
  const p = (key: string, opts?: Record<string, unknown>) => t(`planSelectionV2.${key}`, opts);
  const router = useRouter();
  const couponCode = (router.query.coupon || router.query.discount) as string | undefined;
  const isGettingPaymentUrl = useAppSelector(s => s.PaymentFormReducer.isGettingPaymentUrl);
  const { activeSubscription, hasPaidSubscription, loading, plans, period, setPeriod, seats, setSeats, yearlySavingPct } = usePlanData();
  const requestedPeriod = periodFromQuery(router.query.period as string | string[] | undefined);
  React.useEffect(() => { if (requestedPeriod) setPeriod(requestedPeriod); }, [requestedPeriod, setPeriod]);
  // Which CTA started the checkout. While the checkout link is being fetched the page stays put:
  // that button spins ("Opening secure checkout…") and the others lock — instead of the whole
  // page dropping to a blank skeleton, which read as a crash.
  const [pendingKey, setPendingKey] = React.useState<string | null>(null);
  const busyKey = isGettingPaymentUrl ? pendingKey : null;
  // #1024 - does the account have a Gateway? Looked up once; a failed lookup never blocks checkout.
  const [getGatewayImpact, { data: gatewayImpact }] = useGetGatewayImpactMutation();
  React.useEffect(() => { void getGatewayImpact(); }, [getGatewayImpact]);
  // #1165 - arrived from the hosted-MCP quota wall: report the pricing view against the journey
  // and, only for the treatment arm, show the MCP-specific intro. The arm lookup ships paused
  // (everyone control), and a failed lookup also resolves to control.
  const mcpContext = React.useMemo(() => parseMcpPricingContext(router.query), [router.query]);
  const [resolveMcpArm] = useResolveMcpPricingArmMutation();
  const [recordMcpEvent] = useRecordMcpEventMutation();
  const [mcpArm, setMcpArm] = React.useState<'control' | 'treatment' | null>(null);
  const mcpShownRef = React.useRef(false);
  React.useEffect(() => {
    if (!mcpContext || mcpShownRef.current) return;
    mcpShownRef.current = true;
    void resolveMcpArm().unwrap().then(toMcpPricingArm, () => toMcpPricingArm(null)).then(arm => {
      setMcpArm(arm);
      void recordMcpEvent({ name: 'mcp_pricing_shown', props: { journeyId: mcpContext.journeyId, mcpClientId: mcpContext.client, arm } });
    });
  }, [mcpContext, resolveMcpArm, recordMcpEvent]);
  const [gatewayWarn, setGatewayWarn] = React.useState<{ planName: string; proceed: () => void } | null>(null);
  const fx = useFxDirector(FX_ORDER, FX_DURATIONS, !loading);

  const yearly = period === 'year';
  const paid = plans.filter(pl => !pl.isFree);
  const free = plans.find(pl => pl.isFree);
  const priceOf = (pl: PlanView) => (yearly ? pl.yearlyPerMonth : pl.monthly);
  const currentPlan = plans.find(pl => pl.cta.isCurrentPlan);
  const guardGatewayLoss = (plan: PlanView) => (proceed: () => void): boolean => {
    if (!shouldWarnGatewayLoss({
      currentIncludesGateway: currentPlan?.features.has('offline-socket') === true,
      targetIncludesGateway: plan.features.has('offline-socket'),
      impact: gatewayImpact,
    })) return false;
    setGatewayWarn({ planName: plan.name, proceed });
    return true;
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '48px 16px' }}>
        <div style={{ height: 40, width: 460, maxWidth: '80%', margin: '0 auto', borderRadius: 10, background: C.track }} />
        <div style={{ height: 16, width: 520, maxWidth: '90%', margin: '16px auto 48px', borderRadius: 8, background: C.track }} />
        <div className="psv2-grid">
          {[0, 1, 2].map(i => <div key={i} style={{ height: 480, borderRadius: 20, background: C.surface, border: `1px solid ${C.border}` }} />)}
        </div>
      </div>
    );
  }

  const seatIndex = Math.max(0, SEAT_OPTIONS.indexOf(seats));
  const stepSeats = (dir: 1 | -1) => setSeats(SEAT_OPTIONS[Math.min(SEAT_OPTIONS.length - 1, Math.max(0, seatIndex + dir))]);

  const ctaLabel = (plan: PlanView) => {
    const { cta, isCurrentPlan } = plan.cta;
    if (isCurrentPlan) return p('ctaCurrent');
    if (cta === 'downgrade') return p('ctaDowngrade', { plan: plan.name });
    if (cta === 'update') return p('ctaUpdate');
    // #766 - a no-card trial: the trial's own tier (and above) is the pay action, lower tiers are a
    // neutral "Get X" (never "Downgrade"/"Switch").
    if (activeSubscription?.status === 'in_trial' && plan.order != null && plan.order < (activeSubscription.order ?? 0)) {
      return p('ctaStart', { plan: plan.name });
    }
    return hasPaidSubscription ? p('ctaUpgrade', { plan: plan.name }) : p('ctaStart', { plan: plan.name });
  };

  const renderCta = (plan: PlanView, tone: 'primary' | 'secondary' | 'night' | 'navy', small = false) => {
    const { cta, isCurrentPlan } = plan.cta;
    const cls = isCurrentPlan ? '' : cta === 'downgrade' ? 'psv2-cta-quiet' : `psv2-cta-${tone}`;
    const busy = busyKey === plan.key;
    const state = busy ? 'psv2-cta-busy' : busyKey ? 'psv2-cta-locked' : '';
    return (
      <div data-testid={small ? undefined : `subscription-upgrade-button-${plan.name}`} onClickCapture={() => setPendingKey(plan.key)} aria-busy={busy || undefined}>
        <ChargebeeButton
          plan={plan.planKey}
          duration={yearly ? 'Yearly' : 'Monthly'}
          label={busy ? p('ctaOpening') : ctaLabel(plan)}
          disabled={isCurrentPlan && activeSubscription?.status === 'active'}
          seats={seats}
          variant="regular"
          size="lg"
          className={`psv2-cta ${small ? 'psv2-cta-sm' : ''} ${cls} ${state}`}
          couponCode={couponCode}
          beforeCheckout={guardGatewayLoss(plan)}
        />
      </div>
    );
  };

  const renderCard = (plan: PlanView, index: number) => {
    const Icon = PLAN_ICON[plan.key] ?? Zap;
    const isCurrent = plan.cta.isCurrentPlan;
    const night = plan.features.has('offline-socket');
    const popular = plan.recommended && !night;
    const price = priceOf(plan);
    const yearlyTotal = Math.round(plan.yearlyPerMonth * 12 * seats);
    const yearlySaving = Math.max(0, Math.round((plan.monthly - plan.yearlyPerMonth) * 12 * seats));
    const prev = index > 0 ? paid[index - 1] : undefined;
    const added = FEATURE_ORDER.filter(f => plan.features.has(f) && !prev?.features.has(f));
    // Only what the very next plan adds — a short, concrete reason to move up, not a wall of dashes.
    const next = paid[index + 1];
    const missing = next ? FEATURE_ORDER.filter(f => next.features.has(f) && !plan.features.has(f)) : [];
    // What always-on costs on top of the plan before it — the number the Pro decision turns on.
    const gap = prev ? Math.max(0, Number((price - priceOf(prev)).toFixed(1))) : 0;

    const text = night ? C.nightText : C.text;
    const muted = night ? C.nightMuted : C.muted;
    const line = night ? C.nightLine : C.border;
    const check = night ? '#5eead4' : C.success;
    const cardFx = fx.stateFor(plan.key);

    return (
      <FxCard
        key={plan.key}
        fxKey={plan.key}
        onEnter={fx.enter}
        onLeave={fx.leave}
        sheen={night ? 'rgba(125,211,252,.16)' : 'color-mix(in srgb, #1677cc 9%, transparent)'}
        className={`psv2-card ${night ? 'psv2-night' : ''} ${popular ? 'psv2-popular' : ''}`}
        data-testid={`subscription-plan-${plan.name}`}
        style={{
          position: 'relative', display: 'flex', flexDirection: 'column', borderRadius: 22, padding: 28,
          background: night ? C.night : C.surface,
          border: night ? '1px solid rgba(255,255,255,.08)' : popular ? `1.5px solid color-mix(in srgb, ${C.brandSolid} 60%, transparent)` : `1px solid ${C.border}`,
          boxShadow: night ? '0 30px 60px -30px rgba(7,26,51,.75)' : popular ? '0 24px 48px -32px rgba(22,119,204,.5)' : 'none',
          color: text,
          overflow: 'hidden',
        }}
      >
        {night && (
          <>
            {/* a living night sky: "it keeps working at night" without a word */}
            <ProSky fx={cardFx} />
            <span aria-hidden style={{ position: 'absolute', top: -60, insetInlineEnd: -60, width: 200, height: 200, borderRadius: 999, background: 'radial-gradient(closest-side, rgba(83,189,235,.35), transparent)' }} />
          </>
        )}

        {/* Chip in the corner, out of the text flow — a long (translated) plan name never has to
            share its line with it, so every card's price starts at the same height. */}
        <div style={{ position: 'absolute', top: 16, insetInlineEnd: 16, zIndex: 1 }}>
          {isCurrent ? (
            <Pill bg={night ? 'rgba(255,255,255,.16)' : C.text} fg={night ? '#fff' : 'var(--color-surface,#fff)'}>{p('badgeCurrent')}</Pill>
          ) : night ? (
            <Pill bg="rgba(94,234,212,.16)" fg="#99f6e4"><Moon size={12} />{p('badgeAlwaysOn')}</Pill>
          ) : popular && !hasPaidSubscription ? (
            <Pill bg={C.brand} fg={C.brandText}>{p('badgePopular')}</Pill>
          ) : null}
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <span style={{ flex: '0 0 auto', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: night ? 'rgba(255,255,255,.12)' : `color-mix(in srgb, ${C.brandSolid} 12%, transparent)`, color: night ? '#fff' : C.brandSolid }}>
            <Icon size={18} />
          </span>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plan.name}</h3>
        </div>
        <p style={{ position: 'relative', margin: '12px 0 0', fontSize: 14, lineHeight: 1.5, color: muted, minHeight: 42 }}>{p(`tagline.${plan.key}`)}</p>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 8, marginTop: 22 }}>
          <span style={{ fontSize: 48, fontWeight: 800, letterSpacing: '-.035em', lineHeight: 1, color: text }}>{money(price)}</span>
          {yearly && plan.monthly > price && <span style={{ fontSize: 18, fontWeight: 600, color: muted, textDecoration: 'line-through' }}>{money(plan.monthly)}</span>}
          <span style={{ fontSize: 14, color: muted }}>{p('perUserMonth')}</span>
        </div>
        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, minHeight: 26, marginTop: 10, fontSize: 13, color: muted }}>
          {yearly ? (
            <>
              <span>{p('perYear', { amount: money(yearlyTotal) })}</span>
              {yearlySaving > 0 && <Pill bg={night ? 'rgba(34,197,94,.42)' : `color-mix(in srgb, ${C.success} 13%, transparent)`} fg={night ? '#ffffff' : C.success}>{p('youSave', { amount: money(yearlySaving) })}</Pill>}
            </>
          ) : <span>{p('billedMonthly')}</span>}
        </div>

        <div style={{ position: 'relative', marginTop: 22 }}>{renderCta(plan, night ? 'night' : popular ? 'primary' : 'secondary')}</div>

        <div style={{ position: 'relative', height: 1, background: line, margin: '22px 0 16px' }} />
        <div style={{ position: 'relative', fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: muted, marginBottom: 12 }}>
          {prev ? p('everythingIn', { plan: prev.name }) : p('includes')}
        </div>
        <ul style={{ position: 'relative', listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
          {added.map(f => {
            // The always-on line IS Pro's pitch: one row, with what it costs over the plan before.
            const cloud = f === 'offline-socket';
            const tint = cloud && night ? '#7dd3fc' : check;
            return (
              <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14.5, color: text, lineHeight: 1.45 }}>
                <span style={{ flex: '0 0 auto', width: 20, height: 20, marginTop: 1, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `color-mix(in srgb, ${tint} 18%, transparent)`, color: tint }}>
                  {cloud ? <Cloud size={12} strokeWidth={2.75} /> : <Check size={13} strokeWidth={3} />}
                </span>
                <span>
                  {cloud ? <strong style={{ fontWeight: 650 }}>{p(`feature.${f}`)}</strong>
                    : f === 'remove-branding' ? <BrandingLine label={p(`feature.${f}`)} fx={cardFx} />
                    : p(`feature.${f}`)}
                  {cloud && gap > 0 && prev && (
                    <span style={{ display: 'block', fontSize: 13, color: muted, marginTop: 3 }}>{p('alwaysOnGap', { amount: money(gap), plan: prev.name })}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        {added.includes('unlimited-messages') && <MessageFountain fx={cardFx} />}
        {night && (
          // Always-on, drawn: laptop closed → cloud → delivered. An illustration of what the plan
          // does, pinned to the bottom like the other cards' "Not included" block.
          <div style={{ position: 'relative', marginTop: 'auto', paddingTop: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '14px 16px', borderRadius: 14, background: 'rgba(255,255,255,.06)', border: `1px solid ${C.nightLine}` }}>
              <Step icon={<LaptopOff size={18} />} label={p('flowOff')} />
              <Dots />
              <Step icon={<Cloud size={18} />} label={p('flowCloud')} glow />
              <Dots />
              <Step icon={<CheckCheck size={18} />} label={p('flowSent')} />
            </div>
          </div>
        )}
        {missing.length > 0 && (
          // What the next plans add — fills the card honestly and shows the reason to move up.
          <div style={{ position: 'relative', marginTop: 'auto', paddingTop: 22 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: muted, marginBottom: 10 }}>{p('notIncluded')}</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {missing.map(f => (
                <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: muted }}>
                  <Minus size={14} style={{ flex: '0 0 auto', opacity: 0.6 }} />{p(`feature.${f}`)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </FxCard>
    );
  };

  // Real values per plan (from the fixed Free limits), not a wall of dashes.
  const compareRows: Array<{ id: string; label: string; value: (pl: PlanView) => React.ReactNode }> = [
    {
      id: 'sending', label: p('row.sending'),
      value: pl => pl.features.has('offline-socket')
        ? <strong style={{ color: C.brandSolid, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Cloud size={15} />{p('val.cloud')}</strong>
        : <span style={{ color: C.muted }}>{p('val.browser')}</span>,
    },
    { id: 'messages', label: p('row.messages'), value: pl => pl.features.has('unlimited-messages') ? p('val.unlimited') : <span style={{ color: C.muted }}>{p('val.oneAtATime')}</span> },
    { id: 'boards', label: p('row.boards'), value: pl => pl.features.has('unlimited-tasks') ? p('val.unlimited') : <span style={{ color: C.muted }}>{p('val.boardsLimit')}</span> },
    {
      id: 'branding', label: p('row.branding'),
      value: pl => pl.features.has('remove-branding')
        ? <Check size={18} strokeWidth={2.75} style={{ color: C.success, display: 'inline' }} aria-label={p('included')} />
        : <Minus size={16} style={{ color: C.trackStrong, display: 'inline' }} aria-label={p('notIncluded')} />,
    },
  ];

  const renderCompare = () => (
    <section style={{ marginTop: 80 }}>
      <h2 style={{ textAlign: 'center', fontSize: 28, fontWeight: 780, letterSpacing: '-.025em', color: C.text, margin: 0 }}>{p('compareTitle')}</h2>
      <div className="psv2-table-wrap" style={{ marginTop: 26, borderRadius: 18, border: `1px solid ${C.border}`, background: C.surface }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'start', padding: '18px 20px', fontWeight: 600, color: C.muted, fontSize: 13 }}>{yearly ? p('pricesYearly') : p('pricesMonthly')}</th>
              {plans.map(pl => {
                const pro = pl.features.has('offline-socket');
                return (
                  <th key={pl.key} style={{ padding: '18px 12px', textAlign: 'center', background: pro ? `color-mix(in srgb, ${C.brandSolid} 7%, transparent)` : undefined }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: C.text, fontSize: 15 }}>
                      {pl.name}
                      {pl.recommended && !pro && !hasPaidSubscription && <Pill bg={C.brand} fg={C.brandText}>{p('badgePopular')}</Pill>}
                      {pro && <Pill bg="#0a2647" fg="#99f6e4"><Moon size={11} />{p('badgeAlwaysOn')}</Pill>}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: C.muted, fontWeight: 500 }}>{pl.isFree ? '$0' : `${money(priceOf(pl))}${p('perUserMonthShort')}`}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {compareRows.map(row => (
              <tr key={row.id} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: '15px 20px', color: C.text, fontWeight: 500 }}>{row.label}</td>
                {plans.map(pl => (
                  <td key={pl.key} style={{ padding: '15px 12px', textAlign: 'center', color: C.text, background: pl.features.has('offline-socket') ? `color-mix(in srgb, ${C.brandSolid} 7%, transparent)` : undefined }}>
                    {row.value(pl)}
                  </td>
                ))}
              </tr>
            ))}
            <tr style={{ borderTop: `1px solid ${C.border}` }}>
              <td />
              {plans.map(pl => (
                <td key={pl.key} style={{ padding: '16px 10px', textAlign: 'center', background: pl.features.has('offline-socket') ? `color-mix(in srgb, ${C.brandSolid} 7%, transparent)` : undefined }}>
                  {pl.isFree ? null : renderCta(pl, pl.features.has('offline-socket') ? 'navy' : pl.recommended ? 'primary' : 'secondary', true)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );

  const showCancel = !!free && shouldShowFreeTileCancelCta({ isCurrentPlan: free.cta.isCurrentPlan, hasPaidSubscription, activeStatus: activeSubscription?.status });

  return (
    <div className="psv2" style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 16px 72px' }}>
      <style>{STYLES + FX_STYLES}</style>
      <GatewayLossDialog
        open={gatewayWarn !== null}
        planName={gatewayWarn?.planName ?? ''}
        pendingCount={gatewayImpact?.pendingCount ?? 0}
        onKeep={() => setGatewayWarn(null)}
        onContinue={() => { const w = gatewayWarn; setGatewayWarn(null); w?.proceed(); }}
      />

      {/* Hero */}
      <header className="psv2-in" style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: 'clamp(32px, 4.2vw, 48px)', fontWeight: 820, letterSpacing: '-.04em', lineHeight: 1.08, color: C.text }}>
          {p('titleLead')}{' '}
          <span style={{ background: 'linear-gradient(90deg, #1677cc, #17a2c4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>{p('titleAccent')}</span>
        </h1>
        <p className="psv2-sub" style={{ margin: '16px auto 0', fontSize: 17, lineHeight: 1.55, color: C.muted, maxWidth: 600 }}>
          {p('subtitle')}
          {/* A quiet link only — nothing near the plan cards or checkout. */}
          <GuideVideoButton lesson="free-or-pro" variant="link" surface="plans_hero" style={{ marginInlineStart: 6, fontSize: 'inherit', fontWeight: 'inherit', height: 'auto' }} />
        </p>
        {mcpContext && mcpArm === 'treatment' && (
          <div data-testid="mcp-pricing-intro" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 20, padding: '9px 16px', borderRadius: 12, fontSize: 13.5, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderInlineStart: `3px solid ${C.brandSolid}` }}>
            <Info size={15} style={{ color: C.brandSolid, flex: '0 0 auto' }} />
            <span><strong>{p('mcpIntro.lead')}</strong> {p('mcpIntro.body')}</span>
          </div>
        )}
        {!hasPaidSubscription && free?.cta.isCurrentPlan && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 20, padding: '9px 16px', borderRadius: 12, fontSize: 13.5, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderInlineStart: `3px solid ${C.brandSolid}` }}>
            <Info size={15} style={{ color: C.brandSolid, flex: '0 0 auto' }} />
            <span><strong>{p('onFreeLead')}</strong> {p('onFreeBody')}</span>
          </div>
        )}
        {hasPaidSubscription && currentPlan && (
          <div style={{ marginTop: 18, fontSize: 14, color: C.muted }}>{p('onPaid', { plan: currentPlan.name })}</div>
        )}
      </header>

      {/* Controls */}
      <div className="psv2-in psv2-controls" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 14, margin: '30px 0 40px' }}>
        <div role="radiogroup" aria-label={p('billing')} style={{ display: 'inline-flex', padding: 4, borderRadius: 999, background: C.surface, border: `1px solid ${C.border}` }}>
          {(['month', 'year'] as const).map(per => {
            const on = period === per;
            return (
              <button key={per} type="button" role="radio" aria-checked={on} onClick={() => setPeriod(per)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 18px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 650, fontFamily: 'inherit', background: on ? C.brandSolid : 'transparent', color: on ? '#ffffff' : C.muted, transition: 'background .15s ease,color .15s ease' }}>
                {per === 'month' ? p('monthly') : p('yearly')}
                {per === 'year' && yearlySavingPct > 0 && (
                  <span style={{ whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: 750, padding: '2px 8px', borderRadius: 999, background: on ? C.success : `color-mix(in srgb, ${C.success} 14%, transparent)`, color: on ? '#fff' : C.success }}>
                    {p('savePct', { pct: yearlySavingPct })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 999, background: C.surface, border: `1px solid ${C.border}` }}>
          <button type="button" className="psv2-step" aria-label={p('fewerUsers')} disabled={seatIndex === 0} onClick={() => stepSeats(-1)}
            style={{ width: 38, height: 38, borderRadius: 999, border: 'none', background: 'transparent', color: C.text, cursor: seatIndex === 0 ? 'default' : 'pointer', opacity: seatIndex === 0 ? 0.35 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Minus size={16} />
          </button>
          <span className="psv2-seat-label" style={{ minWidth: 92, textAlign: 'center', fontSize: 14, fontWeight: 650, color: C.text }} aria-live="polite">{p('users', { count: seats })}</span>
          <button type="button" className="psv2-step" aria-label={p('moreUsers')} disabled={seatIndex === SEAT_OPTIONS.length - 1} onClick={() => stepSeats(1)}
            style={{ width: 38, height: 38, borderRadius: 999, border: 'none', background: 'transparent', color: C.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Plans */}
      <div ref={fx.rootRef} className="psv2-grid psv2-in">{paid.map(renderCard)}</div>

      {/* Trust — right under the buttons it reassures */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 28px', marginTop: 24, fontSize: 13.5, color: C.muted }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Lock size={15} />{p('trustSecure')}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><RefreshCw size={15} />{p('trustChange')}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Check size={15} />{p('trustCancel')}</span>
      </div>

      {showCancel && (
        <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 13.5, color: C.muted }}>
          <span>{p('freeFallback')}</span>
          <div data-testid="cancel-subscription-cta">
            <ChargebeeButton plan="Free" duration="Monthly" label={p('cancelSubscription')} disabled={false} seats={seats} variant="outline" className="psv2-link-btn" />
          </div>
        </div>
      )}

      {renderCompare()}

      {/* FAQ */}
      <section style={{ marginTop: 80, maxWidth: 760, marginInline: 'auto' }}>
        <h2 style={{ textAlign: 'center', fontSize: 28, fontWeight: 780, letterSpacing: '-.025em', color: C.text, margin: '0 0 18px' }}>{p('faqTitle')}</h2>
        {['alwaysOn', 'perUser', 'change', 'cancel'].map((id, i) => <Faq key={id} q={p(`faq.${id}.q`)} a={p(`faq.${id}.a`)} defaultOpen={i === 0} />)}
      </section>
    </div>
  );
};

const Step: React.FC<{ icon: React.ReactNode; label: string; glow?: boolean }> = ({ icon, label, glow }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
    <span style={{ width: 38, height: 38, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', color: glow ? '#0a2647' : '#e0f2fe', background: glow ? '#7dd3fc' : 'rgba(255,255,255,.1)', boxShadow: glow ? '0 0 22px rgba(125,211,252,.55)' : 'none' }}>{icon}</span>
    <span style={{ fontSize: 11.5, fontWeight: 600, color: C.nightMuted, textAlign: 'center', lineHeight: 1.25 }}>{label}</span>
  </div>
);

const Dots: React.FC = () => (
  <span aria-hidden className="psv2-flow" style={{ flex: 1, minWidth: 18, height: 3, marginBottom: 20, borderRadius: 2, backgroundImage: 'radial-gradient(circle, rgba(125,211,252,.95) 1.2px, transparent 1.6px)', backgroundSize: '8px 3px', backgroundRepeat: 'repeat-x' }} />
);

/** Marks only the part that matters (No "Powered by Blueticks"), not the trailing words. */
const BrandingLine: React.FC<{ label: string; fx: React.ComponentProps<typeof MarkerHighlight>['fx'] }> = ({ label, fx }) => {
  const end = label.indexOf('Blueticks"');
  if (end < 0) return <MarkerHighlight fx={fx}>{label}</MarkerHighlight>;
  const cut = end + 'Blueticks"'.length;
  return <><MarkerHighlight fx={fx}>{label.slice(0, cut)}</MarkerHighlight>{label.slice(cut)}</>;
};

const Pill: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: bg, color: fg, whiteSpace: 'nowrap' }}>{children}</span>
);

const Faq: React.FC<{ q: string; a: string; defaultOpen?: boolean }> = ({ q, a, defaultOpen }) => (
  <details className="psv2-faq" open={defaultOpen} style={{ borderBottom: `1px solid ${C.border}`, padding: '18px 4px' }}>
    <summary style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, fontSize: 16, fontWeight: 600, color: C.text }}>
      {q}
      <ChevronDown size={18} className="psv2-chev" style={{ color: C.muted, flex: '0 0 auto' }} />
    </summary>
    <p style={{ margin: '10px 0 0', fontSize: 14.5, lineHeight: 1.65, color: C.muted }}>{a}</p>
  </details>
);

export default PlanSelectionV2;
